import type { Funnel } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import './funnel-chart.css';
import { LowSampleBadge } from '../../shared/ui/LowSampleBadge.js';

interface FunnelChartProps {
  funnel: Funnel | undefined;
  loading: boolean;
  onSelectStep?: (stepId: string) => void;
}

const percent = (ratio: number) => `${(ratio * 100).toFixed(1).replace('.0', '')} %`;
const seconds = (ms: number) => (ms === 0 ? '—' : `${(ms / 1000).toFixed(ms >= 10_000 ? 0 : 1)} s`);

/**
 * Funnel of a journey, step by step (functional §5.4 "Vue par parcours"): how
 * many sessions entered each step, how many left there, and the field they
 * touched last. The bar is drawn from the API figures; nothing is computed here.
 */
export function FunnelChart({ funnel, loading, onSelectStep }: FunnelChartProps) {
  if (loading && !funnel) return <p className="chart-empty">Chargement de l'entonnoir…</p>;
  if (!funnel || funnel.steps.length === 0) {
    return <p className="chart-empty">Aucune session mesurée sur ce parcours pour les filtres choisis.</p>;
  }

  const reference = Math.max(funnel.enteredSessions, ...funnel.steps.map((step) => step.enteredSessions), 1);

  return (
    <div className="funnel">
      <header className="funnel__summary">
        <span>
          <strong>{funnel.enteredSessions.toLocaleString('fr-FR')}</strong> sessions entrées
        </span>
        <span>
          <strong>{funnel.completedSessions.toLocaleString('fr-FR')}</strong> achevées ·{' '}
          {percent(funnel.completionRate)}
        </span>
      </header>
      <ol className="funnel__list">
        {funnel.steps.map((step) => (
          <li
            key={step.stepId}
            className={`funnel__row ${step.isMajorityExit ? 'funnel__row--exit' : ''}`}
            onClick={onSelectStep ? () => onSelectStep(step.stepId) : undefined}
            data-step-id={step.stepId}
          >
            <div className="funnel__label">
              <span className="funnel__position">{step.position}</span>
              <span className="funnel__name">{step.stepName}</span>
              {step.isMajorityExit && <Badge tone="negative">Sortie majoritaire</Badge>}
              {step.lowConfidence && <LowSampleBadge />}
            </div>
            <div className="funnel__bar" role="img" aria-label={`${step.enteredSessions} sessions`}>
              <div
                className="funnel__fill"
                style={{ width: `${Math.max(2, (step.enteredSessions / reference) * 100)}%` }}
              />
              <div
                className="funnel__loss"
                style={{ width: `${(step.exitedSessions / reference) * 100}%` }}
                title={`${step.exitedSessions} abandons`}
              />
            </div>
            <dl className="funnel__facts">
              <div>
                <dt>Sessions</dt>
                <dd>{step.enteredSessions.toLocaleString('fr-FR')}</dd>
              </div>
              <div>
                <dt>Abandon</dt>
                <dd className={step.dropOffRate >= 0.2 ? 'funnel__negative' : undefined}>
                  {percent(step.dropOffRate)}
                </dd>
              </div>
              <div>
                <dt>Temps médian</dt>
                <dd>{seconds(step.medianDurationMs)}</dd>
              </div>
              <div>
                <dt>Friction</dt>
                <dd>{step.frictionScore}</dd>
              </div>
              <div>
                <dt>Dernier champ avant sortie</dt>
                <dd>{step.majorityExitFieldKey ?? '—'}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ol>
    </div>
  );
}
